import ProtectedRoute from '@/components/ProtectedRoute.tsx';
import {AuthProvider} from '@/context/AuthContext.tsx';
import {DocumentProvider} from '@/context/DocumentContext.tsx';
import {helpers} from '@/helpers/index.ts';
import {ClerkProvider} from '@clerk/clerk-react';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Toaster} from 'sonner';
import homePaths from "@/constants/homePaths.js";
import {TooltipProvider} from "@/components/ui/tooltip.js";
import ReactGA from "react-ga4";
import * as Sentry from "@sentry/react";

export default function App() {
  const [initLoading, setInitLoading] = useState(true);
  const navigate = useNavigate();

  const init = async () => {
    if (import.meta.env.PROD) {
      ReactGA.initialize('G-XD9VK7ER4N');
      startSentry();
    }
    await helpers.api.init();
    setInitLoading(false);
  };

  const startSentry = () => {
    const DEBUG_MODE = import.meta.env.VITE_DEBUG_MODE === "true";
    if (DEBUG_MODE) {
      return;
    }
    Sentry.init({
      dsn: "https://d30e7da2dd1337c8459c1d961954d5fe@o4508953805848576.ingest.us.sentry.io/4508959094276096",
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration()
      ],
      // Set `environment` to `development` or `production` based on your environment
      environment: import.meta.env.VITE_DEBUG_MODE ? "development" : "production",
      // Tracing
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ["localhost", /^https:\/\/kayai.server\.io\/api/],
      // Session Replay
      // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysSessionSampleRate: 0.1,
      // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      replaysOnErrorSampleRate: 1.0,
      // Capture 100% of the profiles
      profilesSampleRate: 1.0,
    });
  }

  useEffect(() => {
    init();
  }, []);

  const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  if (!CLERK_PUBLISHABLE_KEY) {
    throw new Error('CLERK_PUBLISHABLE_KEY is not set in .env');
  }

  if (initLoading) {
    return (
      <div className="">
        <div className="flex min-h-screen items-center justify-center">
          <h1 className="text-4xl font-bold">Loading...</h1>
        </div>
      </div>
    );
  }

  return (
    <div className='tracking-wide'>
      <ClerkProvider
        afterSignOutUrl={homePaths.login}
        publishableKey={CLERK_PUBLISHABLE_KEY}
        routerPush={(to) => navigate(to)}
        routerReplace={(to) => navigate(to, {replace: true})}
      >
        <AuthProvider>
          <DocumentProvider>
            <TooltipProvider delayDuration={0}>
              <ProtectedRoute/>
              <Toaster
                closeButton
                position="top-right"
                richColors
              />
            </TooltipProvider>
          </DocumentProvider>
        </AuthProvider>
      </ClerkProvider>
    </div>
  );
}
